import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { apiUrl } from '../utils/apiBase';

const BackgroundImageManager = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    description: '',
    sort_order: 0
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await fetch(apiUrl('/admin/background-images'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      
      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        window.location.href = '/admin/login';
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setImages(data.data);
      } else {
        toast.error('Failed to fetch background images');
      }
    } catch (error) {
      console.error('Error fetching images:', error);
      toast.error('Error fetching background images');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    
    try {
      const response = await fetch(apiUrl('/admin/background-images/upload'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: formData
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        window.location.href = '/admin/login';
        throw new Error('Unauthorized');
      }
      
      if (response.ok) {
        const data = await response.json();
        return data.data.url; // Return the uploaded image URL
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    
    try {
      let imageUrl = formData.url;
      
      // If a file is selected, upload it first
      if (selectedFile) {
        imageUrl = await handleFileUpload(selectedFile);
      }
      
      const url = editingImage 
        ? apiUrl(`/admin/background-images/${editingImage.id}`)
        : apiUrl('/admin/background-images');
      
      const method = editingImage ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify({
          ...formData,
          url: imageUrl
        })
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        window.location.href = '/admin/login';
        return;
      }
      
      if (response.ok) {
        toast.success(editingImage ? 'Background image updated!' : 'Background image added!');
        setShowAddForm(false);
        setEditingImage(null);
        setFormData({ name: '', url: '', description: '', sort_order: 0 });
        setSelectedFile(null);
        fetchImages();
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to save background image');
      }
    } catch (error) {
      console.error('Error saving image:', error);
      toast.error('Error saving background image');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this background image?')) {
      return;
    }
    
    try {
      const response = await fetch(apiUrl(`/admin/background-images/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        window.location.href = '/admin/login';
        return;
      }
      
      if (response.ok) {
        toast.success('Background image deleted!');
        fetchImages();
      } else {
        toast.error('Failed to delete background image');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Error deleting background image');
    }
  };

  const handleSetActive = async (id) => {
    try {
      const response = await fetch(apiUrl(`/admin/background-images/${id}/set-active`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        window.location.href = '/admin/login';
        return;
      }
      
      if (response.ok) {
        toast.success('Background image set as active!');
        fetchImages();
      } else {
        toast.error('Failed to set active background image');
      }
    } catch (error) {
      console.error('Error setting active:', error);
      toast.error('Error setting active background image');
    }
  };

  const handleEdit = (image) => {
    setEditingImage(image);
    setFormData({
      name: image.name,
      url: image.url,
      description: image.description || '',
      sort_order: image.sort_order
    });
    setSelectedFile(null);
    setShowAddForm(true);
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingImage(null);
    setFormData({ name: '', url: '', description: '', sort_order: 0 });
    setSelectedFile(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Background Image Management</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Add New Image
        </button>
      </div>

      {showAddForm && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-semibold mb-4">
            {editingImage ? 'Edit Background Image' : 'Add New Background Image'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Upload Image or Enter URL
              </label>
              <div className="space-y-3">
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setSelectedFile(file);
                        setFormData({...formData, url: ''}); // Clear URL when file is selected
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {selectedFile && (
                    <p className="text-sm text-green-600 mt-1">
                      Selected: {selectedFile.name}
                    </p>
                  )}
                </div>
                <div className="text-center text-gray-500">OR</div>
                <div>
                  <input
                    type="url"
                    value={formData.url}
                    onChange={(e) => {
                      setFormData({...formData, url: e.target.value});
                      if (e.target.value) {
                        setSelectedFile(null); // Clear file when URL is entered
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
              </div>
              {!selectedFile && !formData.url && (
                <p className="text-sm text-red-600 mt-1">
                  Please either upload a file or enter a URL
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows="3"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sort Order
              </label>
              <input
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="0"
              />
            </div>
            
            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={uploading || (!selectedFile && !formData.url)}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition-colors flex items-center"
              >
                {uploading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                )}
                {uploading ? 'Uploading...' : (editingImage ? 'Update' : 'Add') + ' Image'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={uploading}
                className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {images.map((image) => (
          <div key={image.id} className="border rounded-lg p-4 bg-white shadow-sm">
            <div className="mb-4">
              <img
                src={image.url}
                alt={image.name}
                className="w-full h-32 object-cover rounded-lg"
                onError={(e) => {
                  // Avoid external placeholder (can be blocked and triggers SW errors)
                  e.target.src = '/logo192.svg';
                }}
              />
            </div>
            
            <div className="mb-3">
              <h3 className="font-semibold text-gray-800">{image.name}</h3>
              {image.description && (
                <p className="text-sm text-gray-600 mt-1">{image.description}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">Sort Order: {image.sort_order}</p>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {image.is_active ? (
                  <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                    Active
                  </span>
                ) : (
                  <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">
                    Inactive
                  </span>
                )}
              </div>
              
              <div className="flex space-x-2">
                {!image.is_active && (
                  <button
                    onClick={() => handleSetActive(image.id)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Set Active
                  </button>
                )}
                <button
                  onClick={() => handleEdit(image)}
                  className="text-yellow-600 hover:text-yellow-800 text-sm font-medium"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(image.id)}
                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {images.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No background images found. Add your first image to get started.
        </div>
      )}
    </div>
  );
};

export default BackgroundImageManager;
